import { createStore, compose, applyMiddleware } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { createBrowserHistory } from 'history';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import Actions from 'flow/actions';
import appConfig from 'app-config';
import { batchedSubscribe } from 'redux-batched-subscribe';
import createRootReducer from 'flow/reducers';
import { saveReduxState, getReduxState } from 'flow/utils/browser-utils';
import { compareLatestMessageAndMessageList } from 'flow/utils/message-utils';
import { createLogger } from 'redux-logger';
import * as Immutable from 'immutable';
import { ctiMiddleware } from 'flow/middleware/cti-middleware';

const SAVE_STATE_INTERVAL_MS = 60 * 1000;

const configureStore = () => {
    const loggerMiddleware = createLogger({
        stateTransformer: (state) => {
            const newState = {};

            for (const i of Object.keys(state)) {
                if (Immutable.Iterable.isIterable(state[i])) {
                    newState[i] = state[i].toJS();
                } else {
                    newState[i] = state[i];
                }
            }

            return newState;
        },
    });

    const logTopMessageMismatch = (store) => (next) => (action) => {
        // Apply the action and save it for return
        const result = next(action);
        const newChatMessage = Immutable.fromJS(action.data);
        if (
            Actions.RESULT_CHAT_MESSAGE.is(action.type) &&
            newChatMessage.get('source') === 'Top'
        ) {
            const sessionKey = action.key;
            // Read the new state of the store after the action.
            // We want to check the key of session.latestMessage and compare it to the largest key in that
            // session's list of messages. If these differ, then the reducer didn't do the right thing.
            const newChatsState = store.getState().chats;

            const comparison = compareLatestMessageAndMessageList(
                newChatsState,
                sessionKey,
            );
            if (
                comparison.isMissingLatestMessage ||
                comparison.isMissingMessagesList ||
                comparison.isKeyMismatch
            ) {
                console.group('Error when reducing message from Top');
                if (comparison.isMissingLatestMessage) {
                    console.error(
                        "We received a message from Top, but 'latestMessage' on session %s is empty",
                        sessionKey,
                    );
                }
                if (comparison.isMissingMessagesList) {
                    console.error(
                        "We received a message from Top, but the session %s doesn't have any messages",
                        sessionKey,
                    );
                }
                if (comparison.isKeyMismatch) {
                    console.error(
                        "The 'latestMessage' field on the session %s is not the same" +
                            " as the latest one in that session's list of messages. The keys are %s and %s respectively",
                        sessionKey,
                        comparison.latestKeyOnSession,
                        comparison.latestKeyOnMessageList,
                    );
                }
                console.groupEnd();
            }
        }
        return result;
    };

    const logApiMessagesMismatch = (store) => (next) => (action) => {
        const result = next(action);
        if (Actions.RESULT_CHAT_MESSAGES.is(action.type)) {
            const sessionKey = action.key;
            const comparison = compareLatestMessageAndMessageList(
                store.getState().chats,
                sessionKey,
            );

            if (comparison.isKeyMismatch) {
                // We don't care about the other errors in this case.
                // It's OK if we get empty lists of chatmessages from the API.
                // It's also OK for there not to be a 'latestMessage' on the session.
                console.group('Error when reducing message lists from the API');
                console.error(
                    "The 'latestMessage' field on the session %s is not the same" +
                        " as the latest one in that session's list of messages. The keys are %s and %s respectively",
                    sessionKey,
                    comparison.latestKeyOnSession,
                    comparison.latestKeyOnMessageList,
                );
                console.groupEnd();
            }
        }
        return result;
    };

    const findFalseStateChangesMiddleware = (store) => (next) => (action) => {
        // This variable will store all actions generating false state changes
        // toghether with how many times they have occured since last reload
        // of the app.
        if (!window.__falseStateChangeActions) {
            console.group('False state change tracking is on');
            // eslint-disable-next-line no-console
            console.log(
                'window.__falseStateChangeActions variable created for keeping track of actions with false state changes',
            );
            console.groupEnd();
            window.__falseStateChangeActions = {};
        }

        const prevState = store.getState();
        const result = next(action);
        const nextState = store.getState();
        // Any actions added to this array will not be warned about in the console.
        // This is to avoid spamming the console with warnings about actions which
        // are hard to fix or actions that are not worth fixing.
        const actionsIgnoredInConsole = [
            'EXTENSION_UPDATE_ACTIVE_PROFILE', // WEB-11542
            'REGISTER_HEADSET_REF',
            'RESULT_CHAT_CONTENT',
            'RESULT_CHAT_MESSAGES',
            'RESULT_CHAT_SESSIONS',
            'RESULT_CONFERENCES',
            'RESULT_CONTACT',
            'RESULT_EDITABLE_CONTACT',
            'RESULT_EXTENSIONS',
            'RESULT_EXTENSIONS_NEXT_SCHEDULED_PROFILES',
            'RESULT_FAVORITES',
            'RESULT_NOTIFICATION_SCHEME',
            'RESULT_PBX_LICENSE',
            'RESULT_PROFILES',
            'RESULT_QUEUE',
            'RESULT_QUEUES',
            'RESULT_CALL_RECORDS_FETCHED',
            'RESULT_QUEUE_STAT_SUMMARY',
            'RESULT_REFERS',
            'RESULT_RINGTONE_SETTINGS',
            'RESULT_RINGTONES_LIST',
            'RESULT_SHARED_EXTENSION',
            'RESULT_SOCIAL_INTEGRATION_SETTINGS',
            'RESULT_TOP_HANGUP_CHANNEL', // Can remove after immutable v4
            'RESULT_USER_SOUNDS',
        ];

        for (const i of Object.keys(prevState)) {
            if (Immutable.Iterable.isIterable(prevState[i])) {
                if (
                    prevState[i] !== nextState[i] &&
                    Immutable.is(prevState[i], nextState[i])
                ) {
                    const nrTimes =
                        window.__falseStateChangeActions[action.type.key] || 0;
                    window.__falseStateChangeActions[action.type.key] =
                        nrTimes + 1;

                    if (
                        actionsIgnoredInConsole.indexOf(action.type.key) === -1
                    ) {
                        console.group(
                            'Warning false change to state: Shallow eq: false deep eq: true',
                        );
                        console.error('reducer:', i);
                        console.error('action type: ', action.type.key);
                        console.error('action: ', action);
                        console.groupEnd();
                    }
                }
            }
        }

        return result;
    };

    const history = createBrowserHistory();

    const middlewares = [];
    middlewares.push(thunkMiddleware);
    middlewares.push(ctiMiddleware);
    if (process.env.REDUX_LOGGER_ENABLED === 'true') {
        middlewares.push(loggerMiddleware);
    }
    if (appConfig.environment === 'dev' || appConfig.environment === 'beta') {
        middlewares.push(logTopMessageMismatch);
        middlewares.push(logApiMessagesMismatch);
    }
    if (appConfig.environment === 'dev') {
        middlewares.push(findFalseStateChangesMiddleware);
    }

    //batchedSubscribe is a store enhancer which can enable batching of subscribe notifications on the store. A callback function is
    //called whenever the store updates, and if the notify function in the callback function is called, subscribers will be notified.
    //We use debounce to avoid calling the notify function on every state update.
    //If wait is 0 and leading is false, func invocation is deferred until to the next tick, similar to setTimeout with a timeout of 0.
    const debounceNotifySubscribers = debounce((notify) => notify(), 0, {
        leading: false,
    });
    const storeEnhancer = compose(
        applyMiddleware(...middlewares),
        batchedSubscribe(debounceNotifySubscribers),
    );
    const store = createStore(
        createRootReducer(),
        getReduxState(),
        storeEnhancer,
    );

    store.subscribe(
        throttle(() => {
            if (store.getState().app.get('state') !== 'ready') {
                return;
            }

            // N.B. 'saveReduxState' does some filtering before the state is persisted.
            // Look in the function to see what's actually saved.
            saveReduxState({
                pbx: store.getState().pbx,
                extensions: store.getState().extensions,
            });
        }, SAVE_STATE_INTERVAL_MS),
    );

    if (module.hot) {
        module.hot.accept('flow/reducers', () => {
            const newRootReducer = require('flow/reducers').default;
            store.replaceReducer(newRootReducer);
        });
    }

    return { store, history };
};

if (process.env.NODE_ENV === 'devel') {
    let cache = {};
    let lastDump = 0;

    window.dumpStore = () => {
        const sizeof = (s) => {
            if ('toJS' in s) {
                return JSON.stringify(s.toJS()).length;
            } else {
                return JSON.stringify(s).length;
            }
        };

        const now = Date.now();
        const deltaTime = lastDump !== 0 ? now - lastDump : 0;

        console.group(`State dump (delta duration: ${deltaTime}ms)`);

        const data = Object.entries(store.getState())
            .filter((entry) => entry[0] !== 'phone') // Circular JSON?
            .map((entry) => {
                const size = sizeof(entry[1]);
                const diff = cache[entry[0]] ? size - cache[entry[0]] : 0;

                return [entry[0], size, diff];
            })
            .sort((x, y) => y[1] - x[1]);

        cache = data.reduce((acc, item) => {
            acc[item[0]] = item[1];
            return acc;
        }, {});

        lastDump = Date.now();

        data.forEach((entry) =>
            console.info(`${entry[0]} ${entry[1]} (delta: ${entry[2]})`),
        );

        console.groupEnd();
    };

    console.info('Dump store state "size" by calling: dumpStore()');
}

const { store, history } = configureStore();

export { configureStore, store, history };
